import { useDataCatalog } from "@/composable/data-catalog";
import { useUnits } from "@/composable/use-units";
import {
	DataType,
	DustDataType,
	Unit,
	UnitClasses,
	VibrationSubType,
	WeatherDataType,
} from "@soft-db/data-formatter";
import { FieldFactory } from "@soft-db/form";
import { Show } from "@soft-db/soft-utils";
import { SoftSelect, Tooltip } from "@soft-db/ui";
import { PropType, cloneVNode, computed, watch } from "vue";
import { useI18n } from "vue-i18n";

export type QueryTypeConfigForm = {
	$typeConfig: {
		type: DataType;
		subType: VibrationSubType | WeatherDataType | DustDataType;
		unit: Unit;
	};
};

type FormField = "type" | "subType" | "unit";

export default defineComponent({
	name: "QueryTypeConfig",
	props: {
		useField: {
			type: Function as PropType<FieldFactory<QueryTypeConfigForm>>,
			required: true,
		},
		readonly: Boolean,
		disabled: [Boolean, Array] as PropType<boolean | FormField[]>,
		disabledReason: String,
	},
	setup(props) {
		const { t } = useI18n();

		const typeField = props.useField("$typeConfig.type", {
			isRequired: true,
		});

		const subTypeField = props.useField("$typeConfig.subType", {
			isRequired: ({ $typeConfig }) =>
				[DataType.VIBRATION, DataType.WEATHER, DataType.DUST].includes($typeConfig?.type as any),
		});

		const unitField = props.useField("$typeConfig.unit", {
			isRequired: true,
		});

		const { possibilities } = useUnits();

		const catalog = useDataCatalog();
		const dataType = catalog.generateMenuEntries(catalog.QueryType);
		const vibrationTypes = catalog.generateMenuEntries(catalog.VibrationSubType);
		const weatherTypes = catalog.generateMenuEntries(catalog.WeatherDataType);

		const subTypesLabel = computed(() => {
			if (typeField.data.value === DataType.VIBRATION) {
				return t("dashboard.vibrationType");
			} else if (typeField.data.value === DataType.WEATHER) {
				return t("dashboard.weatherType");
			}
		});

		const subTypes = computed(() => {
			if (typeField.data.value === DataType.VIBRATION) {
				return vibrationTypes;
			} else if (typeField.data.value === DataType.WEATHER) {
				return weatherTypes;
			}
		});

		const hasSubType = computed(() => {
			return [DataType.VIBRATION, DataType.WEATHER].includes(typeField.data.value as any);
		});

		watch(typeField.data, (type, oldType) => {
			if (oldType && type !== oldType) {
				subTypeField.data.value = undefined;
				unitField.data.value = undefined;
			}
			if (type === DataType.DUST) {
				subTypeField.data.value = DustDataType.CONCENTRATION;
			}
		});

		watch(subTypeField.data, () => {
			if (!possibilities(subTypeField.data.value || "").includes(unitField.data.value || "")) {
				unitField.data.value = undefined;
			}
		});

		const dataUnits = computed(() => {
			const type = typeField.data.value;
			const subType = subTypeField.data.value;
			if (!type) return [];

			switch (type as DataType | "metric") {
				case "metric":
					return [];
				case DataType.VIBRATION:
				case DataType.WEATHER:
					return possibilities(subType || "");
				case DataType.DUST:
					return possibilities("concentration");
				default:
					return possibilities(type as UnitClasses);
			}
		});

		watch(
			[typeField.data, subTypeField.data, unitField.data],
			() => {
				if (!unitField.data.value && dataUnits.value.length === 1) {
					unitField.data.value = dataUnits.value.at(0) as any;
				}
			},
			{
				flush: "post",
			}
		);

		function withDisabledTooltip(node: JSX.Element, disabled: boolean) {
			return (
				<Tooltip tooltip={props.disabledReason} disabled={!disabled || !props.disabledReason}>
					<span>{cloneVNode(node, { disabled })}</span>
				</Tooltip>
			);
		}

		function isFieldDisabled(field: FormField) {
			return (
				props.disabled === true || (Array.isArray(props.disabled) && props.disabled.includes(field))
			);
		}

		return () => (
			<div class="auto-grid gap-2">
				{withDisabledTooltip(
					<SoftSelect
						v-model={typeField.data.value}
						label={t("common.type")}
						items={dataType}
						itemText="name"
						itemValue="value"
						readonly={props.readonly}
						errors={typeField.errors.value}
					/>,
					isFieldDisabled("type")
				)}
				<Show when={hasSubType.value}>
					{withDisabledTooltip(
						<SoftSelect
							v-model={subTypeField.data.value}
							label={subTypesLabel.value}
							items={subTypes.value || []}
							itemText="name"
							itemValue="value"
							readonly={props.readonly}
							errors={subTypeField.errors.value}
						/>,
						isFieldDisabled("subType")
					)}
				</Show>
				{withDisabledTooltip(
					<SoftSelect
						v-model={unitField.data.value}
						label={t("dashboard.unit")}
						items={dataUnits.value as string[]}
						readonly={props.readonly}
						errors={unitField.errors.value}
					/>,
					dataUnits.value.length < 2 || isFieldDisabled("unit")
				)}
			</div>
		);
	},
});
